import { useEffect } from "react";

const CATEGORIES = ["All", "Electronics", "Fashion", "Home", "Beauty", "Sports", "Books", "Summer"];

const SORT_OPTIONS = [
  { value: "default", label: "Default", icon: "ri-layout-grid-line" },
  { value: "price-asc", label: "Price: Low to High", icon: "ri-sort-asc" },
  { value: "price-desc", label: "Price: High to Low", icon: "ri-sort-desc" },
  { value: "discount", label: "Biggest Discount", icon: "ri-percent-line" },
  { value: "new-arrival", label: "New Arrivals First", icon: "ri-sparkling-line" },
  { value: "best-seller", label: "Best Sellers", icon: "ri-fire-line" },
];

const CATEGORY_ICONS: Record<string, string> = {
  All: "ri-apps-line",
  Electronics: "ri-smartphone-line",
  Fashion: "ri-t-shirt-line",
  Home: "ri-home-line",
  Beauty: "ri-heart-line",
  Sports: "ri-run-line",
  Books: "ri-book-open-line",
  Summer: "ri-sun-line",
};

interface NewArrivalsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  totalCount: number;
  filteredCount: number;
}

export default function NewArrivalsFilterDrawer({
  open,
  onClose,
  activeCategory,
  onCategoryChange,
  sortBy,
  onSortChange,
  totalCount,
  filteredCount,
}: NewArrivalsFilterDrawerProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="lg:hidden fixed inset-0 z-50 bg-black/40"
        onClick={onClose}
        style={{ backdropFilter: "blur(2px)" }}
      />

      {/* Drawer panel — slides in from right */}
      <div
        className="lg:hidden fixed top-0 right-0 bottom-0 z-50 bg-white flex flex-col"
        style={{
          width: "min(85vw, 340px)",
          animation: "slideInRight 0.26s cubic-bezier(0.32,0.72,0,1)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">Filter &amp; Sort</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Showing {filteredCount} of {totalCount} products
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Sort section */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Sort By</p>
            <div className="space-y-1.5">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onSortChange(opt.value)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all active:scale-98"
                  style={
                    sortBy === opt.value
                      ? {
                          background: "linear-gradient(135deg, rgba(184,134,11,0.12), rgba(255,215,0,0.12))",
                          color: "#92400e",
                          border: "1.5px solid rgba(184,134,11,0.35)",
                        }
                      : {
                          background: "#f9fafb",
                          color: "#6b7280",
                          border: "1.5px solid transparent",
                        }
                  }
                >
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{
                      background: sortBy === opt.value ? "rgba(184,134,11,0.15)" : "#f3f4f6",
                    }}
                  >
                    <i
                      className={`${opt.icon} text-sm`}
                      style={{ color: sortBy === opt.value ? "#B8860B" : "#9ca3af" }}
                    />
                  </div>
                  {opt.label}
                  {sortBy === opt.value && (
                    <i className="ri-check-line ml-auto text-sm" style={{ color: "#B8860B" }} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Category section */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Category</p>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => onCategoryChange(cat)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all active:scale-95"
                  style={
                    activeCategory === cat
                      ? {
                          background: "linear-gradient(135deg, #B8860B, #FFD700)",
                          color: "#1a0f00",
                        }
                      : {
                          background: "#f9fafb",
                          color: "#6b7280",
                          border: "1px solid #e5e7eb",
                        }
                  }
                >
                  <i className={`${CATEGORY_ICONS[cat]} text-sm flex-shrink-0`} />
                  <span className="truncate">{cat}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-3.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all active:scale-95"
            style={{
              background: "linear-gradient(135deg, #B8860B, #FFD700)",
              color: "#1a0f00",
            }}
          >
            Show {filteredCount} Products
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
